import { useEffect, useState } from "react";
import { getUserDetails } from "../services/network";

// Module-level so the cache and batching are shared across every component
// instance, not just within one — the same underwriter/processor id commonly
// shows up on many waivers on one page.
const nameCache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

// Ids waiting on the next batched lookup, and the resolver for each one's
// promise. Batching (rather than one request per id) means a page rendering
// many waivers at once fires a single bulk request instead of N.
let pendingIds: string[] = [];
let pendingResolvers = new Map<string, (name: string) => void>();
let flushScheduled = false;

// Test-only escape hatch: this cache/queue is intentionally module-level/
// global, which would otherwise leak resolved names or pending batches
// across unrelated test cases that happen to reuse the same fixture id.
export const __resetUserDisplayNameCacheForTests = (): void => {
  nameCache.clear();
  inFlight.clear();
  pendingIds = [];
  pendingResolvers = new Map();
  flushScheduled = false;
};

const buildDisplayName = (data: any, fallback: string): string => {
  const name = [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim();
  return name || data?.username || fallback;
};

const flushPendingLookups = async (): Promise<void> => {
  const ids = pendingIds;
  const resolvers = pendingResolvers;
  pendingIds = [];
  pendingResolvers = new Map();
  flushScheduled = false;

  // async/try-catch (not .then/.catch) so this can't throw synchronously and
  // crash the caller — a badly-mocked or misconfigured network call should
  // fail soft to the raw id, same as everywhere else in this app.
  try {
    const { data } = await getUserDetails(ids);
    // /online-sizer/users doesn't key success and failure entries the same
    // way: a resolved profile has been seen keyed by `id`, while an entry
    // it couldn't resolve comes back as { userId, error: "Failed to fetch
    // Keycloak user ..." } — keyed by `userId` instead. Check both so a
    // successful lookup is never missed just because it isn't the shape the
    // most recently-seen error happened to use.
    const profilesById = new Map<string, any>(
      (data ?? []).map((profile: any) => [profile?.id ?? profile?.userId, profile]),
    );
    ids.forEach((userId) => {
      const name = buildDisplayName(profilesById.get(userId), userId);
      nameCache.set(userId, name);
      resolvers.get(userId)?.(name);
      inFlight.delete(userId);
    });
  } catch (err) {
    console.error("Something went wrong while resolving user display names. ERROR:", err);
    ids.forEach((userId) => {
      resolvers.get(userId)?.(userId);
      inFlight.delete(userId);
    });
  }
};

const resolveUserDisplayName = (userId: string): Promise<string> => {
  const cached = nameCache.get(userId);
  if (cached) return Promise.resolve(cached);

  const existing = inFlight.get(userId);
  if (existing) return existing;

  const promise = new Promise<string>((resolve) => {
    pendingIds.push(userId);
    pendingResolvers.set(userId, resolve);
  });

  inFlight.set(userId, promise);

  if (!flushScheduled) {
    flushScheduled = true;
    setTimeout(flushPendingLookups, 0);
  }

  return promise;
};

// Resolves a Keycloak userId (e.g. a waiver's createdBy/responseBy) to
// "First Last" for display, falling back to the account's username or the
// raw id if the lookup fails or the profile has no name set. Shows the raw
// id immediately so the UI never blocks/blanks on this, then swaps in the
// resolved name once the lookup completes. Lookups for ids requested around
// the same time are batched into a single network call.
export const useUserDisplayName = (userId: string | undefined): string => {
  const [displayName, setDisplayName] = useState<string>(userId ?? "");

  useEffect(() => {
    if (!userId) {
      setDisplayName("");
      return;
    }

    const cached = nameCache.get(userId);
    if (cached) {
      setDisplayName(cached);
      return;
    }

    setDisplayName(userId);
    let isCurrent = true;
    resolveUserDisplayName(userId).then((name) => {
      if (isCurrent) setDisplayName(name);
    });
    return () => {
      isCurrent = false;
    };
  }, [userId]);

  return displayName;
};
